import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, BookOpenIcon, CheckCircle2Icon, CompassIcon, HandHeartIcon, MonitorIcon, PaletteIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const pillars = [{
  icon: CompassIcon,
  title: 'التعلّم الأساسي',
  body: 'الأساس الذي يعرّف الطفل بذاته، وبكيفية تواصله مع الآخرين، وكيف يحمل نفسه بثقة هادئة.',
  points: ['الهوية والثقة', 'المهارات الاجتماعية والأخلاق', 'الاندماج دون عزلة', 'أدوات التواصل والقيادة'],
  tone: 'sage' as const
}, {
  icon: BookOpenIcon,
  title: 'الإيمان الإسلامي والهوية الثقافية',
  body: 'أساس مناسب للعمر في الإيمان والممارسة الإسلامية، إلى جانب صلة قوية بالتراث الثقافي، يُبنى بحكمة ولطف.',
  points: ['الإيمان والممارسة الإسلامية في الحياة اليومية', 'أساسيات اللغة العربية', 'القرآن: أساسيات التلاوة والمعاني', 'الهوية الثقافية وتراث المجتمع'],
  tone: 'rose' as const
}, {
  icon: PaletteIcon,
  title: 'الإثراء والهوايات',
  body: 'مهارات وإبداع يعززان الثقة والتعبير والفضول إلى جانب المنهج الأساسي.',
  points: ['اللغات', 'الخط', 'دروس إبداعية', 'مشاريع'],
  tone: 'sand' as const
}];
const ageGroups = [{
  emoji: '🌱',
  title: 'من 5 إلى 7 سنوات',
  program: 'أسس الهوية والانتماء',
  focus: 'مساعدة الأطفال الصغار على الشعور بالأمان والثقة والفخر بمن هم.',
  tone: 'sage' as const,
  columns: [{
    title: 'التعلّم الأساسي',
    items: ['الوعي بالذات والمشاعر', 'اللطف والاحترام والتعاطف', 'الثقة في التعبير عن الهوية']
  }, {
    title: 'الإيمان والممارسة الإسلامية',
    items: ['القيم الإسلامية وقصص القرآن: تعلّم مبادئ الإيمان من خلال القصص النبوية الجذابة.', 'تعلّم القرآن والعربية: الحروف العربية الأساسية والتعرّف على الحروف والنطق السليم للأصوات.', 'الصلاة والآداب: مقدمة لخطوات الصلاة اليومية وحركاتها البسيطة وآداب الإسلام.']
  }, {
    title: 'الإثراء والهوايات',
    items: ['الخط العربي الإبداعي', 'الفن والقصص واللعب الإبداعي', 'الأناشيد والإيقاع']
  }]
}, {
  emoji: '🌿',
  title: 'من 8 إلى 10 سنوات',
  program: 'الهوية والثقة',
  focus: 'دعم الأطفال مع ازدياد وعيهم الاجتماعي واستقلاليتهم.',
  tone: 'sky' as const,
  columns: [{
    title: 'التعلّم الأساسي',
    items: ['الهوية واحترام الذات', 'التواصل والصداقات', 'التعامل مع الاختلافات']
  }, {
    title: 'الإيمان والممارسة الإسلامية',
    items: ['إقامة الصلاة: إرشاد عملي ومواظبة على أداء الصلوات الخمس.', 'قراءة القرآن والعربية: إتقان أساسيات التجويد والنطق والقراءة السليمة من المصحف.', 'أسس العقيدة: فهم أركان الإيمان وصفات الله تعالى.']
  }, {
    title: 'الإثراء والهوايات',
    items: ['الخط العربي', 'مسارات اللغات الأوروبية', 'الكتابة الإبداعية ورواية القصص']
  }]
}, {
  emoji: '🌳',
  title: 'من 11 إلى 14 سنة',
  program: 'القوة والمسؤولية والانتماء',
  focus: 'إعداد المراهقين المبكرين واليافعين لتحديات الحياة الاجتماعية والهوية الواقعية.',
  tone: 'rose' as const,
  columns: [{
    title: 'التعلّم الأساسي',
    items: ['الهوية في المجتمع غير المسلم', 'الحدود واحترام الذات', 'التفكير النقدي والأخلاق']
  }, {
    title: 'الإيمان والممارسة الإسلامية',
    items: ['فقه العبادة: القواعد العملية للطهارة والصلاة مع الانتقال إلى مرحلة البلوغ.', 'تلاوة القرآن ومعانيه: تحسين سلاسة التلاوة والحفظ وفهم أخلاق القرآن.', 'العقيدة والتاريخ الإسلامي: تقوية أصول العقيدة ودراسة محطات أساسية من الحضارة الإسلامية.']
  }, {
    title: 'الإثراء والهوايات',
    items: ['الخط المتقدم', 'الخطابة', 'الإعلام الإبداعي: كتابة وفيديو ومشاريع']
  }]
}, {
  emoji: '🌍',
  title: 'من 15 إلى 18 سنة',
  program: 'القيادة والهوية ومهارات الحياة',
  focus: 'دعم المراهقين في انتقالهم إلى مرحلة البلوغ بثقة ومسؤولية.',
  tone: 'sand' as const,
  columns: [{
    title: 'التعلّم الأساسي',
    items: ['الهوية والهدف والقيادة', 'اتخاذ القرارات الأخلاقية', 'التمثيل والمسؤولية']
  }, {
    title: 'الإيمان والممارسة الإسلامية',
    items: ['الفقه التطبيقي والرؤية الإسلامية للعالم: فهم الحلال والحرام في المعاملات اليومية والأخلاق المعاصرة ونمط الحياة.', 'الدفاع عن الإيمان: تناول الشبهات المعاصرة وبناء هوية فكرية إسلامية راسخة.', 'القرآن والصلة الروحية: الحفاظ على علاقة يومية بالقرآن عبر التلاوة والتدبر.']
  }, {
    title: 'الإثراء والهوايات',
    items: ['مسارات لغوية متقدمة', 'المناظرة والحوار', 'مشاريع ثقافية وبحثية']
  }]
}];
const childGains = ['ثقة هادئة بمن هم', 'أساس إسلامي قوي يناسب أعمارهم', 'مهارات تواصل واجتماع للبيئات المتنوعة', 'مرونة في مواجهة الضغط أو سوء الفهم', 'مساحات إبداعية تعزز التعبير عن الذات', 'طريق إلى نجاح مستدام عبر اندماج واثق'];
export function ArabicKidsPrograms() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2947" data-mp="19e8v6s9twncu">
      <section className="relative overflow-hidden" data-id="element-2948" data-mp="1een2g83lvt9t">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-2949" data-mp="1ectsojxp4sxl" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-2950" data-mp="3g6t4d6eqdcat" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-2951" data-mp="3l4ul3kooozj4">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-8" data-id="element-2952" data-mp="3l77cspk8uo9d">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col gap-6 lg:col-span-6" data-id="element-2953" data-mp="3d4yd4rm3fpcr">
              <span className="text-[15px] font-semibold text-forest-700" data-id="element-2954" data-mp="3n4ffaz24zl3z">تعليم يبني الهوية والثقة والانتماء</span>
              <h1 className="font-display text-4xl font-semibold leading-[1.45] tracking-tight text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-2955" data-mp="3i5uil9m3fjka">برامج الأطفال</h1>
              <p className="max-w-xl text-base leading-loose text-ink-700 md:text-lg" data-id="element-2956" data-mp="3s0w6610q0dkr">تجمع برامجنا للأطفال بين ثلاثة عناصر أساسية: الهوية ومهارات الحياة، والأسس الإسلامية والثقافية، واللغات والإثراء الإبداعي.</p>
              <div className="flex flex-wrap gap-3 pt-2" data-id="element-2957" data-mp="4q67oh8s30df">
                <Link to="#age-groups" data-id="element-2958" data-mp="jlvxyrkzxzny"><Button variant="primary" size="lg" data-id="element-2959" data-mp="jlcj5jqn7re5">عرض البرامج</Button></Link>
                <Link to="/ar/children/scholarships" data-id="element-2960" data-mp="enayiudkjk0k"><Button variant="secondary" size="lg" data-id="element-2961" data-mp="eoc8evium0gp">المنح والدعم</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2962" data-mp="y6qal9kf2anm">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-2963" data-mp="ocra0ezzqyxs">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/ppTjcSFE6RXTZ38TUFMDbN/Kids-2.jpg" alt="مجموعة من الأطفال المسلمين يقرأون كتابًا مصورًا معًا" data-id="element-2964" data-mp="tbuik9f6jnai" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-sage-50/60 py-16 md:py-24" data-id="element-2965" data-mp="5l8cuosnef3o">
        <PatternDecor side="left" y="top" size="lg" data-id="element-2966" data-mp="akbgc4szcd9j" />
        <Container data-id="element-2967" data-mp="agvxqs03km3d">
          <h2 className="max-w-3xl font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2968" data-mp="3rrndsws308kq">كيف تُنظَّم برامجنا</h2>
          <p className="mt-5 max-w-4xl leading-loose text-ink-700" data-id="element-2969" data-mp="jrbks694qmk">يتيح هذا النهج المتوازن للأطفال المسلمين الاندماج بثقة في المجتمع، مع البقاء راسخين في إيمانهم الإسلامي والاعتزاز بتراثهم الثقافي.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2970" data-mp="c2tjz9e253up">
            {pillars.map((pillar, index) => <PillarCard key={pillar.title} pillar={pillar} index={index} data-id="element-2971" data-mp="7147dpb6si4i" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2972" data-mp="232lyxqag6pv">
        <Container data-id="element-2973" data-mp="3t9vnk2ozu7ux">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-2974" data-mp="3taf7foy7f6bl">
            <div className="lg:col-span-6" data-id="element-2975" data-mp="3oidqhpq3crf8">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-2976" data-mp="3ohm4n6maq825">ما الذي يكتسبه الأطفال</h2>
              <CheckList points={childGains} size="large" data-id="element-2977" data-mp="3fi7anx6x7fcw" />
            </div>
            <div className="lg:col-span-6" data-id="element-2978" data-mp="3fg4fyzutoy1u">
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-card" data-id="element-2979" data-mp="3ajdxy6zgjsds">
                <img src="https://cdn.magicpatterns.com/uploads/h3APRV42sKgSqaH4rUg3ZZ/Children-Gain.jpg" alt="طلاب مسلمون يتعاونون حول جهاز لوحي ومواد دراسية" className="h-full w-full object-cover" data-id="element-2980" data-mp="35kb139tmssgw" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section id="age-groups" className="scroll-mt-24 bg-[#F7EDE9]/60 py-16 md:py-24" data-id="element-2981" data-mp="35eag8uiav4rv">
        <Container data-id="element-2982" data-mp="35cfxnmui3oqy">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2983" data-mp="3ad3oa7eti3kn">مسارات بحسب العمر</h2>
          <div className="mt-5 max-w-5xl space-y-3 leading-loose text-ink-700" data-id="element-2984" data-mp="2cqo1qr7jafq8">
            <p data-id="element-2985" data-mp="2ho6fqw0erl53">تتبع كل فئة عمرية منهجًا منظّمًا يتكيّف مع احتياجات الأطفال العاطفية والنمائية.</p>
            <p data-id="element-2986" data-mp="2mn97xfvahwvn"><strong className="text-ink-900" data-id="element-2987" data-mp="2mn9c7s2boj1g">ملاحظة حول صيغ المجموعات:</strong> ترحب مسارات الطفولة المبكرة من 5 إلى 10 سنوات بمجموعات مختلطة لتشجيع التعلّم بين الأقران. أما برامج ما قبل المراهقة والمراهقة، من 11 إلى 14 ومن 15 إلى 18 سنة، فتُقدَّم في بيئات مخصّصة حسب الجنس احترامًا للخصوصية والإرشادات الإسلامية للأطفال الأكبر سنًا.</p>
          </div>
          <div className="mt-12 space-y-6" data-id="element-2988" data-mp="1yw3tn54zzcng">
            {ageGroups.map((group, index) => <AgeGroupCard key={group.title} group={group} index={index} data-id="element-2989" data-mp="39kplzcr4ulzm" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-2990" data-mp="3emey6vq3d5pa">
        <PatternDecor side="right" y="middle" size="md" data-id="element-2991" data-mp="3jk6ndhzcqdps" />
        <Container data-id="element-2992" data-mp="3nla6cdcp4pv8">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2993" data-mp="3njp7ibjcmkrk">الخطوات التالية</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-2994" data-mp="2qsn1fqbdelk3">تُشرح التفاصيل الأخرى في صفحات مخصّصة، لتتمكن من اتخاذ قرارك بسرعة وثقة.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-2995" data-mp="2zuw9m9j622ub">
            <NextStepCard icon={MonitorIcon} title="التعلّم عبر الإنترنت" description="كيفية عمل الدروس، ومعايير السلامة، وحجم المجموعات، وإطلاع الوالدين." to="/ar/children/online-learning" tone="sky" index={0} data-id="element-2996" data-mp="2ztbh0pgewtz3" />
            <NextStepCard icon={HandHeartIcon} title="المنح الدراسية" description="الأهلية، والإجراءات، والشفافية، وكيفية تخصيص المقاعد المدعومة من المانحين." to="/ar/children/scholarships" tone="sand" index={1} data-id="element-2997" data-mp="2c5tvhbnqqs4k" />
          </div>
        </Container>
      </section>

      <section className="bg-sand-50/60 py-16 md:py-24" data-id="element-2998" data-mp="1znr69jch6qc6">
        <Container data-id="element-2999" data-mp="1vl4l21ab83ny">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-3000" data-mp="1vkl5usc1ntdm">
            <div className="max-w-xl" data-id="element-3001" data-mp="1qlhxoz0rp71f">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3002" data-mp="1qonnyhl4ueyq">هل أنتم مستعدون للبدء؟</h2>
              <p className="mt-5 leading-loose text-ink-700" data-id="element-3003" data-mp="1gx016tzitm3l">اختر برنامجًا يناسب عمر طفلك واحتياجاته، وابدأ ببناء الثقة والقيم والانتماء في مساحة تعليمية آمنة.</p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-3004" data-mp="1bvamn4d3aqar">
              <Link to="#age-groups" data-id="element-3005" data-mp="17xcz05yywc9h"><Button variant="primary" data-id="element-3006" data-mp="17xwihr49n8pm">عرض البرامج حسب العمر</Button></Link>
              <Link to="/ar/children/scholarships" data-id="element-3007" data-mp="12vnm0ks6poir"><Button variant="secondary" data-id="element-3008" data-mp="12z1sa43yyhvh">المنح الدراسية</Button></Link>
            </div>
          </div>
        </Container>
      </section>
    </div>;
}
function PillarCard({
  pillar,
  index
}: {
  pillar: (typeof pillars)[number];
  index: number;
}) {
  const Icon = pillar.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[pillar.tone]}`} data-id="element-3009" data-mp="tao3ef7u6j8d">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-3010" data-mp="170hqarzs6tfg"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-3011" data-mp="1bz2rouqrrj5h" /></span>
      <h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3012" data-mp="x9wufcbhs308">{pillar.title}</h3>
      <p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-3013" data-mp="128zra9hbj2x4">{pillar.body}</p>
      <CheckList points={pillar.points} compact data-id="element-3014" data-mp="1pz3uwguib2o4" />
    </motion.article>;
}
function AgeGroupCard({
  group,
  index
}: {
  group: (typeof ageGroups)[number];
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`rounded-2xl border p-6 shadow-soft md:p-10 ${toneClasses[group.tone]}`} data-id="element-3015" data-mp="3fghgdm4lkku3">
      <div className="flex items-center gap-3" data-id="element-3016" data-mp="3p8n6eqbags6n">
        <span className="text-2xl" aria-hidden="true" data-id="element-3017" data-mp="3k77kkw1aehn7">{group.emoji}</span>
        <h3 className="font-display text-xl font-semibold leading-loose text-ink-900 md:text-2xl" data-id="element-3018" data-mp="2whvguboqb1sa">{group.title}</h3>
      </div>
      <div className="mt-6 space-y-2 leading-loose text-ink-700" data-id="element-3019" data-mp="2rjlk3wncvt4k">
        <p data-id="element-3020" data-mp="35il6exnebvxk"><strong className="text-ink-900" data-id="element-3021" data-mp="35il8d36yi607">البرنامج:</strong> {group.program}</p>
        <p data-id="element-3022" data-mp="35ggubsfcfqm7"><strong className="text-ink-900" data-id="element-3023" data-mp="35ggw9xywm0ou">محور البرنامج:</strong> {group.focus}</p>
      </div>
      <div className="mt-8 grid gap-8 md:grid-cols-3" data-id="element-3024" data-mp="31j2mo57b9qwb">
        {group.columns.map(column => <div key={column.title} data-id="element-3025" data-mp="kufbcl7k53be">
            <h4 className="mb-4 text-sm font-semibold leading-loose text-ink-900" data-id="element-3026" data-mp="gra98jre33na">{column.title}</h4>
            <CheckList points={column.items} compact data-id="element-3027" data-mp="1c1ffewfxa3ws" />
          </div>)}
      </div>
    </motion.article>;
}
function NextStepCard({
  icon: Icon,
  title,
  description,
  to,
  tone,
  index
}: {
  icon: typeof MonitorIcon;
  title: string;
  description: string;
  to: string;
  tone: Tone;
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-7 shadow-soft md:p-8 ${toneClasses[tone]}`} data-id="element-3028" data-mp="1q0ymdlwzz97i">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-3029" data-mp="1yz0k9slfplqk"><Icon size={20} strokeWidth={1.6} aria-hidden="true" data-id="element-3030" data-mp="28r6mdv5ehjjh" /></span>
      <h3 className="mt-5 font-display text-2xl font-semibold leading-loose text-ink-900" data-id="element-3031" data-mp="28qodnz1jxcuo">{title}</h3>
      <p className="mt-3 leading-loose text-ink-700" data-id="element-3032" data-mp="2ds43s5il69jx">{description}</p>
      <Link to={to} className="mt-5 inline-flex items-center gap-1.5 font-medium text-forest-700 transition hover:gap-2" data-id="element-3033" data-mp="2iqfd8f4yyi1m">عرض التفاصيل <ArrowLeftIcon size={16} strokeWidth={1.6} aria-hidden="true" data-id="element-3034" data-mp="73bul3cibn9t" /></Link>
    </motion.article>;
}
function CheckList({
  points,
  compact = false,
  size = 'default'
}: {
  points: string[];
  compact?: boolean;
  size?: 'default' | 'large';
}) {
  return <ul className={`${compact ? 'mt-4 space-y-2' : 'mt-6 space-y-4'} text-ink-700`} data-id="element-3035" data-mp="3oano5k8hmyfq">
      {points.map(point => <li key={point} className={`flex items-start gap-3 ${size === 'large' ? 'leading-loose' : 'text-sm leading-loose'}`} data-id="element-3036" data-mp="prgu1ym4sm2t">
          <CheckCircle2Icon size={size === 'large' ? 18 : 14} className="mt-1 shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-3037" data-mp="ku766nee4ih3" />
          <span data-id="element-3038" data-mp="b3vo7wxvg8r7">{point}</span>
        </li>)}
    </ul>;
}